import {
  CouponDurationUnit,
  CoverageWithPricesInfo,
  ElementTracker,
  ElementTrackerType,
  FeatureManager,
  MembershipType,
  useViewport,
} from '@enaratech/funnel-helper';
import { Box, Collapse, Divider, FormControlLabelProps, Grid } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import ArrowExpandIcon from 'src/components/Common/Icons/ArrowExpandIcon';
import CheckIcon from 'src/components/Common/Icons/CheckIcon';
import EnaraListItemIcon from 'src/components/Common/Icons/EnaraListItemIcon';
import RadioButtons from 'src/components/Common/Inputs/RadioButtons/RadioButtons';
import { useAuth } from 'src/contexts/auth';
import { useClinic } from 'src/contexts/clinic';
import { useOnboarding } from 'src/contexts/onboarding';
import { useRoutePath } from 'src/hooks/useRoutePath';
import { displayDiscountMessage } from 'src/pages/Payment/helpers';
import { PaymentMode, useMembershipStore } from 'src/store/memberships';
import { getEffectiveProgramType } from 'src/utils/programType';
import PromoInput from './Inputs/PromoInput/PromoInput';

type Props = {
  planOptions: (Pick<FormControlLabelProps, 'label' | 'value'> & Partial<FormControlLabelProps>)[];
  onCoverageChange: (coverage: CoverageWithPricesInfo | null) => void;
  initialCoverage: CoverageWithPricesInfo;
};

const CoverageResult: FC<Props> = ({ planOptions, onCoverageChange, initialCoverage }) => {
  const [visible, setVisible] = useState(false);
  const [annualDiscountEnabled, setAnnualDiscountEnabled] = useState<boolean>(false);
  const [coverage, setCoverage] = useState<CoverageWithPricesInfo | null>(null);
  const [couponCode, setCouponCode] = useState<string | null>(null);

  const routePath = useRoutePath();
  const { isTabletView, isMobileView } = useViewport();
  const {
    authState: { user },
  } = useAuth();
  const { clinicState } = useClinic();
  const {
    onboardingState: {
      eligibility: { membershipType },
    },
  } = useOnboarding();

  const isMonthlyPlan = useMembershipStore((s) => s.isMonthly);
  const paymentMode = useMembershipStore((s) => s.paymentMode);
  const setPaymentMode = useMembershipStore((s) => s.updatePaymentMode);

  const handleChangePaymentMode = (paymentMode: PaymentMode): void => {
    setPaymentMode(paymentMode);
  };

  const handleChangePromo = (code: string | null, data: CoverageWithPricesInfo | null) => {
    setCouponCode(code);
    setCoverage(data || initialCoverage);
    onCoverageChange(data || initialCoverage);
  };

  useEffect(() => {
    (async () => {
      setCoverage(initialCoverage);
      setAnnualDiscountEnabled(
        await FeatureManager.isAnnualDiscountEnabled(
          clinicState!.details.clinicId,
          membershipType ?? MembershipType.Cash
        )
      );
    })();
  }, []);

  return (
    <>
      {couponCode && (
        <div className='details-discount-wrapper'>
          <div className='details-discount-message'>
            {displayDiscountMessage(
              coverage,
              isMonthlyPlan ? CouponDurationUnit.Monthly : CouponDurationUnit.Yearly
            )}
          </div>
        </div>
      )}
      <div className='details-payment'>
        {annualDiscountEnabled && (
          <p className='details-title-payment'>Choose Annual or Monthly Payment</p>
        )}
        <Box
          display='flex'
          justifyContent='center'
          flexWrap='wrap'
          columnGap={3}
          className='plan-payment'>
          {annualDiscountEnabled && (
            <>
              <div className='coverage-bubble'>{isMonthlyPlan ? 'Save 20%' : 'Best choice'}</div>
              <RadioButtons
                name='Payment Plan'
                id='payment-planType'
                onChange={(e, value) => handleChangePaymentMode(value as PaymentMode)}
                options={planOptions}
                value={paymentMode}
              />
            </>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <p className='details-title-payment' style={{ marginTop: '20px', marginBottom: '5px' }}>
              Coupon Code
            </p>
            <Grid item xs={12} sm={12} key={'promo-payment'} className='inputs-payment'>
              <PromoInput
                handleChange={handleChangePromo}
                clinicId={clinicState!.details!.clinicId}
                membershipType={membershipType}
                programType={getEffectiveProgramType({ user, clinic: clinicState })!}
              />
            </Grid>
          </div>
        </Box>
        {couponCode ? (
          <h3 className={`coverage-price ${couponCode ? 'coverage-discount' : ''}`}>
            {isMonthlyPlan
              ? `$${coverage?.originalPrices.monthly}/Month`
              : `$${coverage?.originalPrices.annual}/Year`}
          </h3>
        ) : (
          <h3 className={`coverage-price ${couponCode ? 'coverage-discount' : ''}`}>
            {isMonthlyPlan
              ? `$${coverage?.discountedPrices.monthly}/Month`
              : `$${coverage?.discountedPrices.annual}/Year`}
          </h3>
        )}

        {couponCode && (
          <h3 className='coverage-discount-new-price'>
            {isMonthlyPlan
              ? `$${coverage?.discountedPrices.monthly}/Month`
              : `$${coverage?.discountedPrices.annual}/Year`}
          </h3>
        )}

        {(isTabletView || isMobileView) && (
          <>
            <Divider />
            <ElementTracker
              routePath={routePath}
              name='Plan coverage details'
              type={ElementTrackerType.Clickable}
              value={visible ? 'close' : 'open'}>
              <div className='coverage-open' onClick={() => setVisible(!visible)}>
                <ArrowExpandIcon
                  color='primary'
                  className={visible ? 'arrow-collapse' : 'arrow-expand'}
                />{' '}
                Membership includes
              </div>
            </ElementTracker>
          </>
        )}

        <Collapse in={isTabletView || isMobileView ? visible : true}>
          <div className='coverage-list-payment coverage-container-payment'>
            {!(isTabletView || isMobileView) && <h3>What includes:</h3>}
            <div
              onClick={() => {
                if (isTabletView || isMobileView) setVisible(!visible);
              }}>
              {coverage?.items.map((item, i) => (
                <li key={`coverage-result-item-${i}`}>
                  <>
                    {item.coveredBy === 'enara' ? <EnaraListItemIcon /> : <CheckIcon />}
                    <span>{item.text}</span>
                  </>
                </li>
              ))}
            </div>
          </div>
          <Divider />
          <div className='coverage-legend-payment coverage-container-payment'>
            <li>
              <CheckIcon />
              <div>
                <span>Covered by your insurance </span>
                <span className='coverage-details'>(copays and deductibles may apply)</span>
              </div>
            </li>
            <li>
              <EnaraListItemIcon />
              <span>Included in your Enara Membership</span>
            </li>
            <li>
              <span className='coverage-details'>
                * Care credit is used to cover copays and deductibles for medical services. Care
                credit can not be used for lab deductibles.
              </span>
            </li>
          </div>
        </Collapse>
      </div>
    </>
  );
};

export default CoverageResult;
